import type { ReactNode } from 'react';
import { Button, cn } from '@insio/ui-toolkit';

/**
 * Shared surface of every AI result card — the same light grey as the assistant
 * bubble, so cards and plain replies read as one family.
 */
export const RESULT_CARD_CLASS =
  'w-full border border-border-base rounded-2xl p-md flex flex-col gap-sm bg-background-base shadow-sm';

interface ResultCardProps {
  children: ReactNode;
  className?: string;
}

/** Card surface shared by all AI result parts. Proposal cards keep Juneau's own styling. */
export function ResultCard({ children, className }: ResultCardProps) {
  return <div className={cn(RESULT_CARD_CLASS, className)}>{children}</div>;
}

interface ResultCardHeaderProps {
  /** Leading headline of the card. */
  title: ReactNode;
  /** Optional trailing slot — badges, a status or a date range. */
  children?: ReactNode;
}

/** Title row shared by every result card: headline left, optional detail right. */
export function ResultCardHeader({ title, children }: ResultCardHeaderProps) {
  return (
    <div className='flex items-center justify-between gap-sm'>
      <span className='font-semibold text-foreground-base'>{title}</span>
      {children}
    </div>
  );
}

interface CardActionsProps {
  children: ReactNode;
}

/** Right-aligned action row — keeps card buttons compact instead of stretched full-width. */
export function CardActions({ children }: CardActionsProps) {
  return <div className='flex flex-wrap items-center justify-end gap-sm'>{children}</div>;
}

interface CardButtonProps {
  children: ReactNode;
  onClick: () => void;
  /** `filled` for the card's main action, `outline` for a supporting one. Both pink. */
  tone?: 'filled' | 'outline';
  disabled?: boolean;
}

/** Action button of a result card — matches the proposal card's pink confirm button. */
export function CardButton({ children, onClick, tone = 'filled', disabled }: CardButtonProps) {
  return (
    <Button
      size='sm'
      variant='secondary'
      outline={tone === 'outline' ? 'secondary' : undefined}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </Button>
  );
}
